import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChildren} from '@angular/core';
import {IVendor} from '../vendor';
import {Subscription} from 'rxjs/Subscription';
import {GenericValidator} from '../../../shared/generic-validator';
import {ActivatedRoute, Router} from '@angular/router';
import {Observable} from 'rxjs/Observable';
import {FormBuilder, FormControl, FormControlName, FormGroup, Validators} from '@angular/forms';
import {VendorService} from '../../../services/vendor.service';
import {Title} from '@angular/platform-browser';

@Component({
  selector: 'app-vendor-edit',
  templateUrl: './vendor-edit.component.html',
  styleUrls: ['./vendor-edit.component.scss']
})
export class VendorEditComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren(FormControlName, { read: ElementRef }) formInputElements: ElementRef[];

    pageTitle = '';
    errorMessage: string;
    vendorForm: FormGroup;

    vendor: IVendor;
    private sub: Subscription;

    // Use with the generic validation message class
    displayMessage: { [key: string]: string } = {};
    private validationMessages: { [key: string]: { [key: string]: string } };
    private genericValidator: GenericValidator;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private vendorService: VendorService,
        private title: Title
    ) {
        this.title.setTitle('EDI - Vendors');
        this.validationMessages = {
            VENDOR_ID: {
                required: 'Vendor name is required.',
                minlength: 'Vendor name must be at least three characters.',
                maxlength: 'Vendor name cannot exceed 50 characters.'
            }
        };

        // Define an instance of the validator for use with this form,
        // passing in this form's set of validation messages.
        this.genericValidator = new GenericValidator(this.validationMessages);
    }

    ngOnInit(): void {
        this.vendorForm = this.fb.group({
            VENDOR_ID: ['', [Validators.required,
                Validators.minLength(3),
                Validators.maxLength(50)]],
            VD1_ID: [''],
            VD1_TRADING_PARTNER_QUALIFIER: [''],
            VD1_TRADING_PARTNER_ID: [''],
            VD1_TRADING_PARTNER_GS_ID: [''],
            VD1_RECEIVER_QUALIFIER: [''],
            VD1_RECEIVER_ID: [''],
            VD1_BANK_ROUTING_NUMBER: [''],
            VD1_BANK_ACCOUNT_NUMBER: [''],
            VD1_AS2_CERTIFICATE_FILENAME: [''],
            VD1_AS2_RECEIVER_ID: [''],
            VD1_AS2_TRADING_PARTNER_ID: [''],
            VD1_FLAG:  [''],
            VD1_EDI_VERSION: [''],
            VD1_X12_STANDARD: [''],
            VD1_SUPPLIER_CODE: [''],
            VD1_DUNS: [''],
            VD1_FACILITY: [''],
            VD1_SEND_EDI_PO: [''],
            VD1_ITEM_USAGE_FORMAT: [''],
            VD1_SEND_EDI_PO_CHANGE: [''],
            VD1_CHECK_P21_EDI_FLAG: [''],
            VD1_PAYMENT_ADVICE_FORMAT: [''],
            VD1_PAYMENT_ADVICE_FORMAT_1: [''],
            Vendor_VD1_PO_FORMAT_1: [''],
            VD1_ITEM_USAGE_FORMAT1: [''],
            VD1_ITEM_USAGE_SOURCE: [''],
            VD1_SEND_FTP: [''],
            VD1_SEND_SFTP: [''],
            VD1_REMOTE_FTP_SERVER: [''],
            VD1_REMOTE_FTP_DIRECTORY_SEND: [''],
            VD1_REMOTE_FTP_DIRECTORY_PICKUP: [''],
            VD1_REMOTE_FTP_USERNAME: [''],
            VD1_REMOTE_FTP_PASSWORD: [''],
            VD1_POST_HTTP: [''],
            VD1_POST_AS2: [''],
            VD1_AS2_REQUEST_RECEIPT: [''],
            VD1_AS2_SIGN_MESSAGES: [''],
            VD1_REMOTE_HTTP_SERVER: [''],
            VD1_AS2_KEY_LENGTH: [''],
            VD1_PICKUP_FTP: [''],
            VD1_FTP_USER: [''],
            VD1_FTP_PASSWORD: [''],
            VD1_FTP_DIRECTORY: [''],
            VD1_RECEIVE_EDI: [''],
            VD1_SEND_ACKNOWLEDGEMENT: [''],
            VD1_PICKUP_SFTP: [''],
            VD1_SHARED_SECRET: [''],
            VD1_RECEIVE_FTP: [''],
            VD1_TEST_MODE: [''],
            VD1_REMOTE_HTTP_USERNAME: [''],
            VD1_REMOTE_HTTP_PASSWORD: [''],
            VD1_PICKUP_DIRECTORY: [''],
            VD1_AS2_ENCRYPTION_ALGORITHM: [''],
            VD1_AS2_COMPATIBILITY_MODE: [''],
            VD1_PAYMENT_QUALIFIER: [''],
            VD1_PAYMENT_ID: [''],
            VD1_MAP: [''],
            VD1_CHECK_ACKNOWLEDGEMENTS: [''],
            VD1_FTP_DOWNLOAD_FILTER: [''],
            VD1_SFTP_PRIVATE_KEY_FILENAME: [''],
        });

        // Read the vendor Id from the route parameter
        this.sub = this.route.params.subscribe(
            params => {
                this.getVendor(params['id'], params['CO1_ID']);
            }
        );
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    ngAfterViewInit(): void {
        // Watch for the blur event from any input element on the form.
        const controlBlurs: Observable<any>[] = this.formInputElements
            .map((formControl: ElementRef) => Observable.fromEvent(formControl.nativeElement, 'blur'));

        // Merge the blur event observable with the valueChanges observable
        Observable.merge(this.vendorForm.valueChanges, ...controlBlurs).debounceTime(800).subscribe(value => {
            this.displayMessage = this.genericValidator.processMessages(this.vendorForm);
        });
    }

    getVendor(id: number, CO1_ID: number): void {
        this.vendorService.getById(id, CO1_ID)
            .subscribe(
                (vendor: IVendor) => this.onVendorRetrieved(vendor),
                (error: any) => this.errorMessage = <any>error
            );
    }

    onVendorRetrieved(vendor: IVendor): void {
        if (this.vendorForm) {
            this.vendorForm.reset();
        }
        this.vendor = vendor[0] || vendor;

        if (this.vendor.VD1_ID === 0) {
            this.pageTitle = 'Add Vendor';
        } else {
            this.pageTitle = `Vendor: ${this.vendor.VENDOR_ID}`;
        }

        // Update the data on the form
        this.vendorForm.patchValue({
            VENDOR_ID:  this.vendor.VENDOR_ID,
            VD1_ID:  this.vendor.VD1_ID,
            VD1_TRADING_PARTNER_QUALIFIER: this.vendor.VD1_TRADING_PARTNER_QUALIFIER,
            VD1_TRADING_PARTNER_ID: this.vendor.VD1_TRADING_PARTNER_ID,
            VD1_TRADING_PARTNER_GS_ID: this.vendor.VD1_TRADING_PARTNER_GS_ID,
            VD1_RECEIVER_QUALIFIER: this.vendor.VD1_RECEIVER_QUALIFIER,
            VD1_RECEIVER_ID: this.vendor.VD1_RECEIVER_ID,
            VD1_BANK_ROUTING_NUMBER: this.vendor.VD1_BANK_ROUTING_NUMBER,
            VD1_BANK_ACCOUNT_NUMBER: this.vendor.VD1_BANK_ACCOUNT_NUMBER,
            VD1_AS2_CERTIFICATE_FILENAME: this.vendor.VD1_AS2_CERTIFICATE_FILENAME,
            VD1_AS2_RECEIVER_ID: this.vendor.VD1_AS2_RECEIVER_ID,
            VD1_AS2_TRADING_PARTNER_ID: this.vendor.VD1_AS2_TRADING_PARTNER_ID,
            VD1_FLAG:  (this.vendor.VD1_FLAG === 'false') ? 0 : 1,
            VD1_EDI_VERSION: this.vendor.VD1_EDI_VERSION,
            VD1_X12_STANDARD: this.vendor.VD1_X12_STANDARD,
            VD1_SUPPLIER_CODE: this.vendor.VD1_SUPPLIER_CODE,
            VD1_DUNS: this.vendor.VD1_DUNS,
            VD1_FACILITY: this.vendor.VD1_FACILITY,
            VD1_SEND_EDI_PO: (this.vendor.VD1_SEND_EDI_PO === 'false') ? 0 : 1, // (this.vendor.VD1_SEND_EDI_PO === '0') ? false : true,
            VD1_ITEM_USAGE_FORMAT: (this.vendor.VD1_ITEM_USAGE_FORMAT === 'false') ? 0 : 1,
            VD1_SEND_EDI_PO_CHANGE: (this.vendor.VD1_SEND_EDI_PO_CHANGE === 'false') ? 0 : 1,
            VD1_CHECK_P21_EDI_FLAG: (this.vendor.VD1_CHECK_P21_EDI_FLAG === 'false') ? 0 : 1,
            VD1_PAYMENT_ADVICE_FORMAT: (this.vendor.VD1_PAYMENT_ADVICE_FORMAT === 'false') ? 0 : 1,
            VD1_ITEM_USAGE_SOURCE: this.vendor.VD1_ITEM_USAGE_SOURCE,
            VD1_SEND_FTP: this.vendor.VD1_SEND_FTP,
            VD1_SEND_SFTP: this.vendor.VD1_SEND_SFTP,
            VD1_REMOTE_FTP_SERVER: this.vendor.VD1_REMOTE_FTP_SERVER,
            VD1_REMOTE_FTP_DIRECTORY_SEND: this.vendor.VD1_REMOTE_FTP_DIRECTORY_SEND,
            VD1_REMOTE_FTP_DIRECTORY_PICKUP: this.vendor.VD1_REMOTE_FTP_DIRECTORY_PICKUP,
            VD1_REMOTE_FTP_USERNAME: this.vendor.VD1_REMOTE_FTP_USERNAME,
            VD1_REMOTE_FTP_PASSWORD: this.vendor.VD1_REMOTE_FTP_PASSWORD,
            VD1_POST_HTTP: this.vendor.VD1_POST_HTTP,
            VD1_POST_AS2: this.vendor.VD1_POST_AS2,
            VD1_AS2_REQUEST_RECEIPT: this.vendor.VD1_AS2_REQUEST_RECEIPT,
            VD1_AS2_SIGN_MESSAGES: this.vendor.VD1_AS2_SIGN_MESSAGES,
            VD1_REMOTE_HTTP_SERVER: this.vendor.VD1_REMOTE_HTTP_SERVER,
            VD1_AS2_KEY_LENGTH: this.vendor.VD1_AS2_KEY_LENGTH,
            VD1_PICKUP_FTP: this.vendor.VD1_PICKUP_FTP,
            VD1_FTP_USER: this.vendor.VD1_FTP_USER,
            VD1_FTP_PASSWORD: this.vendor.VD1_FTP_PASSWORD,
            VD1_FTP_DIRECTORY: this.vendor.VD1_FTP_DIRECTORY,
            VD1_RECEIVE_EDI: this.vendor.VD1_RECEIVE_EDI,
            VD1_SEND_ACKNOWLEDGEMENT: this.vendor.VD1_SEND_ACKNOWLEDGEMENT,
            VD1_PICKUP_SFTP: this.vendor.VD1_PICKUP_SFTP,
            VD1_SHARED_SECRET: this.vendor.VD1_SHARED_SECRET,
            VD1_RECEIVE_FTP: this.vendor.VD1_RECEIVE_FTP,
            VD1_TEST_MODE: this.vendor.VD1_TEST_MODE,
            VD1_REMOTE_HTTP_USERNAME: this.vendor.VD1_REMOTE_HTTP_USERNAME,
            VD1_REMOTE_HTTP_PASSWORD: this.vendor.VD1_REMOTE_HTTP_PASSWORD,
            VD1_PICKUP_DIRECTORY: this.vendor.VD1_PICKUP_DIRECTORY,
            VD1_AS2_ENCRYPTION_ALGORITHM: this.vendor.VD1_AS2_ENCRYPTION_ALGORITHM,
            VD1_AS2_COMPATIBILITY_MODE: this.vendor.VD1_AS2_COMPATIBILITY_MODE,
            VD1_PAYMENT_QUALIFIER: this.vendor.VD1_PAYMENT_QUALIFIER,
            VD1_PAYMENT_ID: this.vendor.VD1_PAYMENT_ID,
            VD1_MAP: this.vendor.VD1_MAP,
            VD1_CHECK_ACKNOWLEDGEMENTS: this.vendor.VD1_CHECK_ACKNOWLEDGEMENTS,
            VD1_FTP_DOWNLOAD_FILTER: this.vendor.VD1_FTP_DOWNLOAD_FILTER,
            VD1_SFTP_PRIVATE_KEY_FILENAME: this.vendor.VD1_SFTP_PRIVATE_KEY_FILENAME,

           // VD1_PAYMENT_ADVICE_FORMAT_1: 'cXML',
            // TODO add other fields
        });
       // this.vendorForm.setControl('tags', this.fb.array(this.vendor.tags || []));
    }

    saveVendor(): void {
        if (this.vendorForm.dirty && this.vendorForm.valid) {
            // Copy the form values over the vendor object values
            const v = Object.assign({}, this.vendor, this.vendorForm.value);

            this.vendorService.save(v, this.pageTitle === 'Add Vendor' )
                .subscribe(
                    () => this.onSaveComplete(),
                    (error: any) => this.errorMessage = <any>error
                );
        } else if (!this.vendorForm.dirty) {
            this.onSaveComplete();
        }
    }

    onSaveComplete(): void {
        // Reset the form to clear the flags
        this.vendorForm.reset();
        this.router.navigate(['/vendors']);
    }

    handleEnableAdviceFormat(value: boolean) {
        const paymentAdviceFormat = this.vendorForm.get('VD1_PAYMENT_ADVICE_FORMAT_1');

        if (this.vendorForm.get('VD1_PAYMENT_ADVICE_FORMAT').value) {
            paymentAdviceFormat.enable();

        } else {
            paymentAdviceFormat.disable();
        }
    }

    handleEnableSendEDIPo(value: boolean) {
        const ediPOFormat = this.vendorForm.get('Vendor_VD1_PO_FORMAT_1');

        if (this.vendorForm.get('VD1_SEND_EDI_PO').value) {
            ediPOFormat.enable();

        } else {
            ediPOFormat.disable();
        }
    }

    handleEnableItemUsageFormat(value: boolean) {
        const itemUsageFormat = this.vendorForm.get('VD1_ITEM_USAGE_FORMAT1');

        if (this.vendorForm.get('VD1_ITEM_USAGE_FORMAT').value) {
            itemUsageFormat.enable();

        } else {
            itemUsageFormat.disable();
        }
    }

    onCancel(){
        this.router.navigate( ['vendors']);
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/vendors/vendor-edit/vendor-edit.component.ts